import { pixelMask, pixelRect } from '@/utils/tilePixels';
import { addTileArtwork, clearTileArtwork } from '@/utils/tileArtwork';
import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import {
  CreateActionsFn,
  CreateDefaultStateFn,
  TileGraphicsDefinition,
  ContextMenuActionFn,
  CreateContextMenuItemsData,
} from '@/models/tiles/TileDefinition';
import { InterfaceTileState, Side, TileSideIOType } from '@/schemas/tiles';
import {
  InterfaceTileDefinition,
  drawInterfaceCompletenessBorder,
  getInterfaceLinkFromTile,
  invalidateInterfaceLinkTiles,
} from './InterfaceTile';
import { Color, Text } from 'pixi.js';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { multiInterfaceBuildAndConnect } from '@/models/logic/utils';
import { TileInstance } from './TileInstance';
import { Direction } from '@/utils/direction';
import { InterfaceType, MIDisplayMode } from '../ChipModel';
import { InterfaceLink } from '../ChipModel';
import { Rotation, rotationToRadians } from '../data/Rotation';
import * as PIXI from 'pixi.js';

export type MultiInterfaceTileStateBase = {};
export type MultiInterfaceTileState = InterfaceTileState<MultiInterfaceTileStateBase>;

export class MultiInterfaceTile extends InterfaceTileDefinition<MultiInterfaceTileStateBase> {
  createActions?: CreateActionsFn<MultiInterfaceTileState> | undefined;
  graphics = multiInterfaceTileGraphicsDefinition;
  type = TileType.MultiInterface;
  protected get interfaceType() {
    return InterfaceType.MULTI;
  }

  name = 'Interface Tile (Multi Signal)';
  canRotate = false;

  createContextMenuItems({
    instance,
    chipEditorModel,
  }: CreateContextMenuItemsData<InterfaceTileState<MultiInterfaceTileStateBase>>): Array<{
    label: string;
    action: ContextMenuActionFn;
  }> {
    const interfaceLink = chipEditorModel.chipModel.interfaceLinkMap.get(
      instance.state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId,
    );

    return [
      {
        label: 'Connection Settings',
        action: (_takeInput, uiModel) => {
          if (!interfaceLink) return;
          uiModel.openMIConnectionConfigDialog(interfaceLink);
        },
      },
      {
        label: 'Display Settings',
        action: (_takeInput, uiModel) => {
          if (!interfaceLink) return;
          uiModel.openMIStateConfigDialog(interfaceLink);
        },
      },
      {
        label: interfaceLink?.labelHidden ? 'Show Label' : 'Hide Label',
        action: () => {
          if (!interfaceLink) return;
          interfaceLink.labelHidden = !interfaceLink.labelHidden;
          invalidateInterfaceLinkTiles(interfaceLink);
        },
      },
      ...super.createContextMenuItems({ instance, chipEditorModel }),
    ];
  }

  buildAndConnectNodes = multiInterfaceBuildAndConnect;

  getGraphBuildVisitKey = (instance: TileInstance, _entryDirection?: Direction) =>
    instance.id;

  createDefaultState: CreateDefaultStateFn<MultiInterfaceTileState> = ({
    meta,
    globalTilePosition,
  }) => {
    return super.createDefaultState({ meta, globalTilePosition });
  };

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
  };
}

const sideSignalKey: Record<Side, keyof typeof Signal extends string ? string : never> = {
  [Side.TOP]: 'top',
  [Side.RIGHT]: 'right',
  [Side.BOTTOM]: 'bottom',
  [Side.LEFT]: 'left',
};

const sideLabelSuffix: Record<Side, string> = {
  [Side.TOP]: 'T',
  [Side.RIGHT]: 'R',
  [Side.BOTTOM]: 'B',
  [Side.LEFT]: 'L',
};

import { computeMIStateIndex } from '@/utils/colorUtils';
import { getMIDisplayColor } from '@/utils/combinedChipColor';

import { getOrCreateTexture } from './tileTextureCache';

export const multiInterfaceTileGraphicsDefinition: TileGraphicsDefinition<MultiInterfaceTileState> =
  {
    type: TileType.MultiInterface,
    draw: function (g, data): void {
      const { state, meta, connections, context, chipEditorModel } = data;
      defaultTileGraphics(g);

      const interfaceLink = getInterfaceLinkFromTile(
        chipEditorModel,
        meta,
        state,
        context ?? { isTileImageGeneration: false },
      );
      const displayMode = interfaceLink?.displayMode ?? MIDisplayMode.NONE;
      const fullBleedDisplay =
        displayMode === MIDisplayMode.GRADIENT || displayMode === MIDisplayMode.PIXEL_ART;

      const ioSides =
        interfaceLink?.type === InterfaceType.MULTI
          ? interfaceLink.computeEffectiveIoSides()
          : {
              [Side.TOP]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
              [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
              [Side.BOTTOM]: {
                type: TileSideIOType.INPUT_OUTPUT,
                forceConnection: false,
              },
              [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
            };

      if (!fullBleedDisplay) {
        connectionGraphics(
          g,
          colors.signalColor,
          colors.noSignalColor,
          meta,
          connections,
          ioSides,
          context,
        );
      }
      let interfaceColor: string = colors.signalColor;
      if (interfaceLink) {
        interfaceColor = interfaceLink.color;
      }

      const dimColor = new Color(interfaceColor)
        .multiply([colors.noSignalValue, colors.noSignalValue, colors.noSignalValue])
        .toHex();

      const tileSize = 120;
      const center = tileSize / 2;

      // Clear children (sprites, text) from previous draw before adding new ones
      clearTileArtwork(g);

      if (displayMode === MIDisplayMode.GRADIENT && interfaceLink) {
        // Gradient display: single filled rect colored by 4-bit state
        const stateIndex = computeMIStateIndex(meta);
        const fillColor = getMIDisplayColor(interfaceLink, stateIndex);

        pixelRect(g, 0, 0, 25, 25, fillColor);
      } else if (displayMode === MIDisplayMode.PIXEL_ART && interfaceLink) {
        // Pixel art display: render pixel art sprite for the current state
        const stateIndex = computeMIStateIndex(meta);
        const pixelArt = interfaceLink.statePixelArt.get(stateIndex);
        if (pixelArt && !pixelArt.isEmpty) {
          // Reuse equal artwork, but never retain a stale image after editing a
          // state (or opening another project with the same interface ID).
          const cacheKey = `pa:${pixelArt.toBase64()}`;
          const tex = getOrCreateTexture(cacheKey, () => pixelArt.toDataUrlSync(4));
          const sprite = new PIXI.Sprite(tex);
          sprite.x = 0;
          sprite.y = 0;
          sprite.width = 120;
          sprite.height = 120;
          addTileArtwork(g, sprite);
        } else {
          // Fallback: show gradient color
          const fillColor = getMIDisplayColor(interfaceLink, stateIndex);

          pixelRect(g, 0, 0, 25, 25, fillColor);
        }
      } else if (displayMode === MIDisplayMode.IMAGE && interfaceLink) {
        // Image display: render a sprite for the current state
        const stateIndex = computeMIStateIndex(meta);
        const imageUrl = interfaceLink.stateImages.get(stateIndex);
        if (imageUrl) {
          const tex = getOrCreateTexture(imageUrl, () => imageUrl);
          const sprite = new PIXI.Sprite(tex);
          sprite.x = 5;
          sprite.y = 5;
          sprite.width = 110;
          sprite.height = 110;
          addTileArtwork(g, sprite);
        } else {
          // Fallback: show gradient color
          const fillColor = getMIDisplayColor(interfaceLink, stateIndex);

          pixelRect(g, 1, 1, 23, 23, fillColor);
        }
      } else {
        // Three logical pixels separate adjacent faces along both diagonals.
        const wedge = [
          '###################',
          '.#################.',
          '..###############..',
          '...#############...',
          '....###########....',
          '.....#########.....',
          '......#######......',
          '.......#####.......',
          '........###........',
          '.........#.........',
        ];
        const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT];
        for (const side of sides) {
          // Skip inactive sides — render nothing
          if (ioSides[side].type === TileSideIOType.NONE) continue;

          const key = sideSignalKey[side];
          const inputHigh = (meta.inputs as Record<string, Signal>)[key] === Signal.HIGH;
          const outputHigh =
            (meta.outputs as Record<string, Signal>)[key] === Signal.HIGH;
          const isHigh = inputHigh || outputHigh;
          const color = isHigh ? interfaceColor : dimColor;

          pixelMask(g, wedge, 3, 1, color, sides.indexOf(side));
        }
      }

      const labelHidden = interfaceLink?.labelHidden ?? false;
      const isHovered = context?.hoveredTileId !== undefined;
      const showLabel = context?.showLabels !== false && (!labelHidden || isHovered);

      const baseLabel = interfaceLink?.label ?? '';
      const sideLabelsMap = interfaceLink?.sideLabels;

      if (showLabel && (baseLabel || sideLabelsMap)) {
        const labelPositions: Record<Side, { x: number; y: number }> = {
          [Side.TOP]: { x: center, y: 22 },
          [Side.RIGHT]: { x: tileSize - 22, y: center },
          [Side.BOTTOM]: { x: center, y: tileSize - 22 },
          [Side.LEFT]: { x: 22, y: center },
        };

        for (const side of [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]) {
          if (ioSides[side].type === TileSideIOType.NONE) continue;
          const pos = labelPositions[side];
          const sideLabel = sideLabelsMap?.[side];
          const text =
            sideLabel ?? (baseLabel ? `${baseLabel}.${sideLabelSuffix[side]}` : '');
          if (!text) continue;
          const labelText = new Text({
            text,
            style: {
              fill: 0xffffff,
              fontSize: 11,
              align: 'center',
            },
          });
          labelText.anchor.set(0.5);
          labelText.x = pos.x;
          labelText.y = pos.y;
          // Counter-rotate label so it stays upright regardless of tile rotation
          labelText.rotation = -rotationToRadians(
            (meta.rotation as Rotation) ?? Rotation.CW_0,
          );
          addTileArtwork(g, labelText);
        }
      }

      if (!fullBleedDisplay) drawInterfaceCompletenessBorder(g, data);
    },
  };
